import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { API } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Truck, Package, Split, Search, Save, Merge, Upload, Download, Plus, Trash2, Warehouse, Undo2, AlertTriangle } from "lucide-react";
import { SkuImage, useShowPhoto } from "@/components/SkuImage";
import { CollapsibleItemRows } from "@/components/CollapsibleItems";
import { TrackingPanel } from "@/components/TrackingPanel";
import { useUiPrefs } from "@/lib/uiPrefs";
import { DensityToggle, tableDensity } from "@/components/ops-ui";

const TABS = [
  { k: "placed", label: "Order Placed", icon: Package, testid: "ct-tab-placed" },
  { k: "shipped", label: "Shipped", icon: Truck, testid: "ct-tab-shipped" },
  { k: "delivered", label: "Delivered to Warehouse", icon: Warehouse, testid: "ct-tab-delivered" },
];

const isBound = (it) => !!(it.warehouse_code && String(it.warehouse_code).trim());

export default function ChinaTracking({ embeddedStage = null, initialQuery = "" }) {
  const [boxes, setBoxes] = useState([]);
  const [tab, setTab] = useState(() => embeddedStage || localStorage.getItem("ct-tab") || "placed");
  const [q, setQ] = useState(initialQuery);
  const [showPhoto, ShowPhotoToggle] = useShowPhoto("china-tracking");
  const { density, setDensity } = useUiPrefs("china-tracking");

  // Tab-1 (Order Placed) working state
  const [editingKey, setEditingKey] = useState(null);
  const [trackDraft, setTrackDraft] = useState("");
  const [splitItem, setSplitItem] = useState(null);
  const [splitQty, setSplitQty] = useState(1);
  const [splitTrack, setSplitTrack] = useState("");
  const [confirmItem, setConfirmItem] = useState(null);
  const [confirmWh, setConfirmWh] = useState("");
  const [confirmCbm, setConfirmCbm] = useState("");
  const [confirmQty, setConfirmQty] = useState("");
  const [bulkSplitBoxKey, setBulkSplitBoxKey] = useState(null);
  const [bulkSplitPick, setBulkSplitPick] = useState({});
  const [selected, setSelected] = useState(new Set());
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeTrack, setMergeTrack] = useState("");
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [deleteBox, setDeleteBox] = useState(null);
  const [deletePw, setDeletePw] = useState("");

  const switchTab = (v) => { setTab(v); localStorage.setItem("ct-tab", v); setSelected(new Set()); };
  const load = () => api.get("/china-tracking").then(r => setBoxes(r.data));
  useEffect(() => { load(); }, []);

  const boxKey = (b) => (b.items[0] && b.items[0].id) || `${b.invoice_id}-${b.order_id}-${b.china_tracking_code}`;

  // ---- Downloads ----
  const downloadFile = async (path, filename) => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(await res.text());
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Download failed"); }
  };

  const downloadCbmExcel = async (box) => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/warehouse-cbm/download-excel`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ item_ids: box.items.map(i => i.id) }),
      });
      if (!res.ok) throw new Error(await res.text());
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `CBM-INPUT-${box.china_tracking_code || box.order_id}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Download failed"); }
  };

  const uploadCbmExcel = async (file) => {
    if (!file) return;
    try {
      const fd = new FormData(); fd.append("file", file);
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/warehouse-cbm/upload-excel`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd,
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.detail || "Upload failed");
      toast.success(`Updated ${j.updated} row(s) · ${j.skipped} skipped`);
      load();
    } catch (e) { toast.error(e.message || "Upload failed"); }
  };

  // ---- Bulk-create (no invoice) ----
  const bulkCreate = async () => {
    if (!bulkFile) return toast.error("Choose an Excel file first");
    try {
      const fd = new FormData(); fd.append("file", bulkFile);
      const { data } = await api.post("/china-tracking/bulk-create", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Created ${data.created} in-transit item(s)${data.sku_auto_created ? ` · ${data.sku_auto_created} new SKU(s)` : ""}${data.errors?.length ? ` · ${data.errors.length} skipped` : ""}`);
      if (data.errors?.length) data.errors.slice(0, 3).forEach(e => toast.warning(`Row ${e.row} (${e.sku}): ${e.error}`));
      setBulkOpen(false); setBulkFile(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk create failed"); }
  };

  // ---- Selection ----
  const toggleBoxSel = (key) => {
    setSelected(prev => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });
  };

  const openBulkDelete = () => {
    const ids = []; let boxCount = 0;
    boxes.forEach(b => { if (selected.has(boxKey(b))) { b.items.forEach(i => ids.push(i.id)); boxCount++; } });
    if (ids.length === 0) return toast.error("No boxes selected");
    setDeleteBox({ label: `${boxCount} box(es) · ${ids.length} item(s)`, ids });
  };

  const doDeleteBox = async () => {
    if (!deleteBox) return;
    if (!deletePw) return toast.error("Password required");
    try {
      const { data } = await api.post("/invoice-items/bulk-delete", { ids: deleteBox.ids, password: deletePw });
      toast.success(`Deleted ${data.deleted} item(s)`);
      setDeleteBox(null); setDeletePw(""); setSelected(new Set()); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  // ---- Tracking / split / merge ----
  const saveTracking = async (box) => {
    if (!trackDraft.trim()) return toast.error("Tracking code required");
    const ids = box.items.map(i => i.id);
    try {
      await api.post("/china-tracking/bulk-set", { item_ids: ids, china_tracking_code: trackDraft.trim() });
      toast.success(`Tracking saved · ${ids.length} item(s)`);
      setEditingKey(null); setTrackDraft(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const revertToPlaced = async (box) => {
    if (!window.confirm(`Move this box back to "Order Placed"? This clears its China tracking code${box.items.some(i => i.warehouse_code) ? ", warehouse code & CBM" : ""}.`)) return;
    const ids = box.items.map(i => i.id);
    try {
      const { data } = await api.post("/china-tracking/revert-to-placed", { item_ids: ids, china_tracking_code: "" });
      toast.success(`Moved back to Order Placed · ${data.modified} item(s)`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Revert failed"); }
  };

  const doSplit = async () => {
    if (!splitItem) return;
    const qty = parseInt(splitQty, 10);
    if (!qty || qty <= 0 || qty > splitItem.quantity) return toast.error(`Split qty must be 1..${splitItem.quantity}`);
    try {
      await api.post(`/invoice-items/${splitItem.id}/split`, { split_quantity: qty, china_tracking_code: splitTrack.trim() });
      toast.success("Item split");
      setSplitItem(null); setSplitQty(1); setSplitTrack(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Split failed"); }
  };

  const doBulkSplit = async () => {
    const entries = Object.entries(bulkSplitPick)
      .map(([item_id, v]) => ({ item_id, split_quantity: parseInt(v, 10) || 0 }))
      .filter(e => e.split_quantity > 0);
    if (entries.length === 0) return toast.error("Enter qty for at least one row");
    try {
      const { data } = await api.post("/china-tracking/bulk-split", { entries });
      toast.success(`Split → ${data.new_order_id}`);
      setBulkSplitBoxKey(null); setBulkSplitPick({}); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk split failed"); }
  };

  const doMergeBack = async (b) => {
    if (!window.confirm(`Merge ${b.order_id} back to its base order-id? Same-SKU rows will be summed.`)) return;
    try {
      const { data } = await api.post("/china-tracking/merge-to-base", { order_id: b.order_id });
      toast.success(`Merged into ${data.base_order_id} · ${data.merged} summed, ${data.moved} moved`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Merge failed"); }
  };

  const isSubBox = (oid) => /-\d{2,}$/.test(oid || "");

  const doMerge = async () => {
    if (!mergeTrack.trim()) return toast.error("Enter a China tracking code to merge under");
    const ids = [];
    boxes.forEach((b) => { if (selected.has(boxKey(b))) b.items.forEach(i => ids.push(i.id)); });
    if (ids.length === 0) return toast.error("No boxes selected");
    try {
      const { data } = await api.post("/china-tracking/merge", { item_ids: ids, china_tracking_code: mergeTrack.trim(), consolidate: true });
      toast.success(`Merged · ${data.tracking_set} items retagged · ${data.rows_merged} duplicate rows consolidated`);
      setMergeOpen(false); setMergeTrack(""); setSelected(new Set()); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Merge failed"); }
  };

  // ---- Inline warehouse/CBM binding (shipped + delivered) ----
  const saveItemField = async (item_id, patch) => {
    try {
      await api.post("/warehouse-cbm/set-item", { item_id, ...patch });
      toast.success("Saved"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  // Mark an item's quantity as needing verification on arrival: keeps it in the order
  // (no forced warehouse split) and highlights its qty on the A5 packing list.
  const toggleArrivalCheck = async (it) => {
    try {
      await api.patch(`/invoice-items/${it.id}/arrival-check`, { needs_arrival_check: !it.needs_arrival_check });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };

  // Toggle Normal / Direct-to-Warehouse inbound type (data flag only; workflow deferred).
  const toggleInboundType = async (it) => {
    const next = it.inbound_type === "direct_to_warehouse" ? "normal" : "direct_to_warehouse";
    try {
      await api.patch(`/inbound/items/${it.id}/inbound-type`, { inbound_type: next });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };

  const doConfirmWarehouse = async () => {
    if (!confirmItem) return;
    const wh = confirmWh.trim();
    if (!wh) return toast.error("Enter a warehouse code");
    const body = { warehouse_code: wh };
    if (confirmCbm !== "") body.cbm = parseFloat(confirmCbm);
    if (confirmQty !== "") {
      const q = parseInt(confirmQty, 10);
      if (!q || q <= 0 || q > confirmItem.quantity) return toast.error(`Quantity must be 1..${confirmItem.quantity}`);
      if (q < confirmItem.quantity) body.quantity = q;
    }
    try {
      const r = await api.post(`/inbound/items/${confirmItem.id}/confirm-warehouse`, body);
      toast.success(r.data.split ? "Split & confirmed to warehouse" : "Confirmed to warehouse");
      setConfirmItem(null); setConfirmWh(""); setConfirmCbm(""); setConfirmQty(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Confirm failed"); }
  };

  // ---- Bucket + filter ----
  const matches = (b) => {
    if (!q) return true;
    const t = q.toLowerCase();
    return (b.order_id || "").toLowerCase().includes(t)
      || (b.china_tracking_code || "").toLowerCase().includes(t)
      || (b.invoice_no || "").toLowerCase().includes(t)
      || (b.warehouse_code || "").toLowerCase().includes(t)
      || b.items.some(i => (i.sku || "").toLowerCase().includes(t) || (i.item_name || "").toLowerCase().includes(t));
  };
  const filtered = boxes.filter(matches);
  const placed = filtered.filter(b => !b.china_tracking_code);
  const shipped = filtered.filter(b => b.china_tracking_code && !b.items.every(isBound));
  const delivered = filtered.filter(b => b.china_tracking_code && b.items.length > 0 && b.items.every(isBound));
  const current = tab === "placed" ? placed : tab === "shipped" ? shipped : delivered;
  const counts = { placed: placed.length, shipped: shipped.length, delivered: delivered.length };
  const needsCheckCount = shipped.reduce((n, b) => n + b.items.filter(i => i.needs_arrival_check).length, 0);

  const colSpan = (showPhoto ? 1 : 0) + (tab === "placed" ? 4 : tab === "shipped" ? 6 : 5);

  const renderRow = (it) => {
    if (tab === "placed") {
      return (
        <tr key={it.id}>
          {showPhoto && <td><SkuImage url={it.image_url} alt={it.sku} size={48} /></td>}
          <td className="font-mono">{it.sku}</td>
          <td className="whitespace-nowrap">{it.item_name}</td>
          <td className="text-right font-mono">{it.quantity}</td>
          <td className="text-right">
            {it.quantity > 1 && (
              <Button size="sm" variant="ghost" onClick={() => { setSplitItem(it); setSplitQty(1); setSplitTrack(""); }} data-testid={`ct-split-${it.id}`}>
                <Split className="w-3.5 h-3.5 mr-1" />Split
              </Button>
            )}
          </td>
        </tr>
      );
    }
    if (tab === "shipped") {
      return (
        <tr key={it.id} data-testid={`ct-shipped-row-${it.id}`} className={it.needs_arrival_check ? "bg-amber-50" : ""}>
          {showPhoto && <td><SkuImage url={it.image_url} alt={it.sku} size={48} /></td>}
          <td className="font-mono">{it.sku}</td>
          <td className="whitespace-nowrap">{it.item_name}</td>
          <td className="text-right font-mono">{it.quantity}</td>
          <td>
            <Input className="h-7 w-28 font-mono text-xs" defaultValue={it.warehouse_code || ""}
              data-testid={`ct-wh-${it.id}`} placeholder="—"
              onBlur={e => {
                const v = e.target.value.trim();
                if (v === (it.warehouse_code || "")) return;
                saveItemField(it.id, { warehouse_code: v });
              }} />
          </td>
          <td className="text-right">
            <Input type="number" step="0.001" className="h-7 w-24 font-mono text-xs text-right"
              defaultValue={it.cbm ?? ""} data-testid={`ct-cbm-${it.id}`} placeholder="—"
              onBlur={e => {
                const raw = e.target.value.trim();
                if (raw === "") return;
                const v = parseFloat(raw);
                if (Number.isNaN(v)) return toast.error("Invalid CBM");
                if (v === it.cbm) return;
                saveItemField(it.id, { cbm: v });
              }} />
          </td>
          <td className="text-right whitespace-nowrap">
            <Button size="sm" variant="ghost" onClick={() => toggleInboundType(it)}
              data-testid={`ct-inbound-type-${it.id}`}
              className={it.inbound_type === "direct_to_warehouse" ? "text-indigo-700 font-semibold" : "text-slate-400"}
              title="Toggle Normal / Direct-to-Warehouse (data flag only for now)">
              {it.inbound_type === "direct_to_warehouse" ? "Direct→WH" : "Normal"}
            </Button>
            {it.needs_arrival_check && (
              <Button size="sm" variant="ghost" onClick={() => { setConfirmItem(it); setConfirmWh(it.warehouse_code || ""); setConfirmCbm(it.cbm ?? ""); setConfirmQty(""); }}
                data-testid={`ct-confirm-wh-${it.id}`} className="text-emerald-700 font-semibold"
                title="Confirm this tracking to a warehouse code (clears the check flag; optionally split part of the qty)">
                <Warehouse className="w-3.5 h-3.5 mr-1" />Confirm → WH
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={() => toggleArrivalCheck(it)}
              data-testid={`ct-arrival-check-${it.id}`}
              className={it.needs_arrival_check ? "text-amber-700 font-semibold" : "text-slate-400"}
              title="Toggle: quantity needs checking on arrival — keeps the item in the order and highlights its qty on the packing list">
              <AlertTriangle className="w-3.5 h-3.5 mr-1" />{it.needs_arrival_check ? "Needs Check" : "Mark Check"}
            </Button>
            {it.quantity > 1 && (
              <Button size="sm" variant="ghost" onClick={() => { setSplitItem(it); setSplitQty(1); setSplitTrack(""); }} data-testid={`ct-split-${it.id}`}>
                <Split className="w-3.5 h-3.5 mr-1" />Split
              </Button>
            )}
          </td>
        </tr>
      );
    }
    // delivered
    return (
      <tr key={it.id}>
        {showPhoto && <td><SkuImage url={it.image_url} alt={it.sku} size={48} /></td>}
        <td className="font-mono">{it.sku}</td>
        <td className="whitespace-nowrap">{it.item_name}</td>
        <td className="text-right font-mono">{it.quantity}</td>
        <td className="font-mono font-semibold text-emerald-700">{it.warehouse_code}</td>
        <td className="text-right font-mono">{it.cbm}</td>
      </tr>
    );
  };

  return (
    <div data-testid="china-tracking-page">
      {tab === "shipped" && needsCheckCount > 0 && (
        <div className="mb-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800" data-testid="ct-needs-check-banner">
          <b>{needsCheckCount}</b> shipped item{needsCheckCount > 1 ? "s" : ""} still need arrival check — confirm each to a warehouse code, or verify the quantity on arrival.
        </div>
      )}
      <header className="mb-4 flex items-end justify-between gap-4 flex-wrap">
        {!embeddedStage && (
        <div>
          <div className="caption">Order Boxes</div>
          <h1 className="font-heading text-3xl font-extrabold mt-1">Stocks in Transit</h1>
          <p className="text-slate-500 mt-1 text-sm">
            Track every order across three stages — from placement, to shipped with China tracking, to delivered and bound to a warehouse code + CBM.
          </p>
        </div>
        )}
        <div className="flex items-center gap-2 flex-wrap">
          {ShowPhotoToggle}
          <DensityToggle density={density} setDensity={setDensity} />
          {tab === "placed" && (
            <Button onClick={() => setBulkOpen(true)} variant="outline" data-testid="ct-bulk-create-btn" className="border-sky-300 text-sky-700 hover:bg-sky-50">
              <Plus className="w-4 h-4 mr-1.5" />Bulk Create (no invoice)
            </Button>
          )}
          {selected.size > 0 && (
            <Button onClick={openBulkDelete} variant="destructive" data-testid="ct-bulk-delete-btn" className="bg-rose-600 hover:bg-rose-700">
              <Trash2 className="w-4 h-4 mr-1.5" />Delete {selected.size} box(es)
            </Button>
          )}
          {tab === "placed" && selected.size > 1 && (
            <Button onClick={() => setMergeOpen(true)} variant="secondary" data-testid="ct-merge-btn">
              <Merge className="w-4 h-4 mr-1.5" />Merge {selected.size} boxes
            </Button>
          )}
          <Search className="w-4 h-4 text-slate-400" />
          <Input placeholder="Search order-id, tracking, invoice, SKU…" value={q} onChange={e => setQ(e.target.value)} className="w-72" data-testid="ct-search" />
        </div>
      </header>

      {/* Tabs */}
      {!embeddedStage && (
      <div className="flex gap-1 mb-4 border-b border-slate-200">
        {TABS.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.k} data-testid={t.testid} onClick={() => switchTab(t.k)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition inline-flex items-center gap-2 ${
                tab === t.k ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-700"
              }`}>
              <Icon className="w-4 h-4" />{t.label}
              <Badge variant="secondary" data-testid={`${t.testid}-count`}>{counts[t.k]}</Badge>
            </button>
          );
        })}
      </div>
      )}

      {current.length === 0 && (
        <div className="text-sm text-slate-400 border border-dashed rounded p-8 text-center" data-testid="ct-empty">
          {tab === "placed" ? "No orders waiting for China tracking."
            : tab === "shipped" ? "No shipped parcels awaiting warehouse & CBM binding."
            : "No delivered parcels yet. Bind warehouse code + CBM on the Shipped tab."}
        </div>
      )}

      <div className="space-y-3">
        {current.map((b, idx) => {
          const key = boxKey(b);
          const isSel = selected.has(key);
          const border = tab === "placed" ? "border-amber-200 bg-amber-50/40"
            : tab === "shipped" ? "border-sky-200 bg-sky-50/30"
            : "border-emerald-200 bg-emerald-50/30";
          return (
            <div key={key} className={`rounded-lg border ${isSel ? "border-sky-400 bg-sky-50" : border}`} data-testid={`ct-box-${tab}-${idx}`}>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-4 py-3 border-b border-slate-100">
                <div className="min-w-0 flex-1 flex items-start gap-3">
                  <input type="checkbox" checked={isSel} onChange={() => toggleBoxSel(key)} className="mt-1" data-testid={`ct-box-sel-${tab}-${idx}`} />
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-xs uppercase tracking-wider px-2 py-0.5 rounded bg-slate-800 text-white">{b.source || "?"}</span>
                      <span className="font-mono text-sm font-bold break-all min-w-0">{b.order_id || <em className="text-slate-400">no order id</em>}</span>
                      {b.china_tracking_code && (
                        <span className="font-mono text-xs px-2 py-0.5 rounded bg-sky-100 text-sky-800">{b.china_tracking_code}</span>
                      )}
                      {tab === "delivered" && b.warehouse_code && (
                        <span className="font-mono text-xs px-2 py-0.5 rounded bg-emerald-100 text-emerald-800">{b.warehouse_code}</span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      Invoice <b>{b.invoice_no || "—"}</b>
                      {b.invoice_id && <Link to={`/invoices/${b.invoice_id}`} className="text-sky-600 hover:underline ml-1" data-testid="ct-view-invoice">(view invoice)</Link>}
                      {" · "}{b.purchase_date || "—"} · {b.billing_company || "—"} · {b.total_qty} pcs
                    </div>
                  </div>
                </div>

                {/* Actions per tab */}
                {tab === "placed" && (
                  editingKey === key ? (
                    <div className="flex flex-wrap items-center gap-2">
                      <Input value={trackDraft} onChange={e => setTrackDraft(e.target.value)} placeholder="China tracking no." className="w-full sm:w-48" data-testid={`ct-input-${idx}`} autoFocus />
                      <Button size="sm" onClick={() => saveTracking(b)} data-testid={`ct-save-${idx}`}><Save className="w-3.5 h-3.5 mr-1" />Save</Button>
                      <Button size="sm" variant="ghost" onClick={() => { setEditingKey(null); setTrackDraft(""); }}>Cancel</Button>
                    </div>
                  ) : (
                    <div className="flex flex-wrap items-center gap-2">
                      {b.items.length > 1 && (
                        <Button size="sm" variant="outline" onClick={() => { setBulkSplitBoxKey(key); setBulkSplitPick({}); }} data-testid={`ct-bulk-split-${idx}`}>
                          <Split className="w-3.5 h-3.5 mr-1" />Bulk split
                        </Button>
                      )}
                      {isSubBox(b.order_id) && (
                        <Button size="sm" variant="outline" onClick={() => doMergeBack(b)} data-testid={`ct-merge-back-${idx}`} className="border-emerald-300 text-emerald-700 hover:bg-emerald-50">
                          <Merge className="w-3.5 h-3.5 mr-1" />Merge back
                        </Button>
                      )}
                      <Button size="sm" onClick={() => { setEditingKey(key); setTrackDraft(""); }} data-testid={`ct-fill-${idx}`}>
                        <Truck className="w-3.5 h-3.5 mr-1" />Fill tracking
                      </Button>
                    </div>
                  )
                )}
                {tab === "shipped" && (
                  <div className="flex flex-wrap items-center gap-2">
                    <Button size="sm" variant="ghost" onClick={() => revertToPlaced(b)} data-testid={`ct-revert-${idx}`}
                      className="text-amber-700 hover:text-amber-800 hover:bg-amber-50">
                      <Undo2 className="w-3.5 h-3.5 mr-1" />Back to Placed
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => downloadCbmExcel(b)} data-testid={`ct-cbm-download-${idx}`}>
                      <Download className="w-3.5 h-3.5 mr-1" />CBM Excel
                    </Button>
                    <label className="inline-flex">
                      <input type="file" accept=".xlsx" className="hidden" data-testid={`ct-cbm-upload-${idx}`}
                        onChange={e => uploadCbmExcel(e.target.files?.[0])} />
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border border-emerald-300 text-emerald-700 hover:bg-emerald-50 cursor-pointer">
                        <Upload className="w-3.5 h-3.5" /> Upload CBM
                      </span>
                    </label>
                  </div>
                )}
                {tab === "delivered" && (
                  <div className="text-xs text-slate-500 font-mono">CBM {b.cbm ?? "—"}</div>
                )}
              </div>

              <div className="overflow-x-auto">
              <table className={`excel-table w-full text-xs ${tableDensity(density)}`}>
                <thead><tr>
                  {showPhoto && <th className="w-14">Photo</th>}
                  <th>SKU</th><th>Item</th><th className="text-right">Qty</th>
                  {tab === "shipped" && <><th className="w-32">Warehouse Code</th><th className="text-right w-28">CBM</th><th className="w-24 text-right">Split</th></>}
                  {tab === "delivered" && <><th>Warehouse Code</th><th className="text-right">CBM</th></>}
                  {tab === "placed" && <th className="w-40 text-right">Split</th>}
                </tr></thead>
                <CollapsibleItemRows items={b.items} colSpan={colSpan} renderRow={renderRow} forceOpen={!!q} testid={`ct-${tab}-${idx}`} />
              </table>
              </div>
              {tab !== "delivered" && b.order_id && (
                <TrackingPanel orderId={b.order_id} source={b.source} defaultOpen={tab === "shipped"} />
              )}
            </div>
          );
        })}
      </div>

      {/* Bulk split dialog */}
      <Dialog open={!!bulkSplitBoxKey} onOpenChange={o => { if (!o) { setBulkSplitBoxKey(null); setBulkSplitPick({}); } }}>
        <DialogContent className="max-w-2xl w-[calc(100vw-2rem)] p-0 gap-0 bg-white max-h-[calc(100dvh-2rem)] flex flex-col overflow-hidden" data-testid="ct-bulk-split-dialog">
          <DialogHeader className="px-6 pt-6 pb-3 shrink-0 bg-white"><DialogTitle>Bulk split into new sub-box</DialogTitle></DialogHeader>
          <div className="px-6 pb-2 flex-1 min-h-0 overflow-y-auto bg-white text-sm space-y-3" data-testid="ct-bulk-split-scroll">
            <p className="text-slate-600">Enter a quantity for each SKU you want to move. All rows land in a single new pending box with an auto-suffixed order-id (<span className="font-mono text-xs bg-slate-100 px-1 rounded">base-01</span> etc.).</p>
            <div className="overflow-x-auto">
              <table className="excel-table w-full text-xs">
                <thead><tr><th>SKU</th><th>Item</th><th className="text-right">Current Qty</th><th className="text-right w-32">Move Qty</th></tr></thead>
                <tbody>
                  {placed.find(b => boxKey(b) === bulkSplitBoxKey)?.items.map(it => (
                    <tr key={it.id}>
                      <td className="font-mono align-top">{it.sku}</td>
                      <td className="break-words min-w-[8rem] max-w-[16rem]">{it.item_name}</td>
                      <td className="text-right font-mono align-top">{it.quantity}</td>
                      <td className="text-right align-top">
                        <Input type="number" min={0} max={it.quantity} className="h-8 w-24 text-right"
                          data-testid={`ct-bulk-qty-${it.id}`} value={bulkSplitPick[it.id] ?? ""}
                          onChange={e => setBulkSplitPick({ ...bulkSplitPick, [it.id]: e.target.value })} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <DialogFooter className="px-6 py-4 shrink-0 border-t border-slate-200 bg-white">
            <Button variant="outline" onClick={() => { setBulkSplitBoxKey(null); setBulkSplitPick({}); }}>Cancel</Button>
            <Button onClick={doBulkSplit} data-testid="ct-bulk-split-confirm" className="bg-sky-600 hover:bg-sky-700">
              <Split className="w-4 h-4 mr-1.5" />Split into new sub-box
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Split dialog */}
      <Dialog open={!!splitItem} onOpenChange={o => { if (!o) { setSplitItem(null); } }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Split item into new box</DialogTitle></DialogHeader>
          {splitItem && (
            <div className="space-y-3 text-sm">
              <div>
                <div className="caption">Item</div>
                <div className="font-mono">{splitItem.sku} · {splitItem.item_name}</div>
                <div className="text-slate-500">Total qty {splitItem.quantity}</div>
              </div>
              <div>
                <label className="caption">Move quantity to new box</label>
                <Input type="number" min={1} max={splitItem.quantity} value={splitQty} onChange={e => setSplitQty(e.target.value)} data-testid="ct-split-qty" />
              </div>
              <div>
                <label className="caption">China tracking for new box (leave blank if still waiting)</label>
                <Input value={splitTrack} onChange={e => setSplitTrack(e.target.value)} placeholder="Blank → new pending box with order-id -01, -02, …" data-testid="ct-split-track" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSplitItem(null)}>Cancel</Button>
            <Button onClick={doSplit} data-testid="ct-split-confirm">Split</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm-to-warehouse dialog (flagged shipped items) */}
      <Dialog open={!!confirmItem} onOpenChange={o => { if (!o) setConfirmItem(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Confirm tracking to warehouse code</DialogTitle></DialogHeader>
          {confirmItem && (
            <div className="space-y-3 text-sm">
              <div>
                <div className="caption">Item</div>
                <div className="font-mono">{confirmItem.sku} · {confirmItem.item_name}</div>
                <div className="text-slate-500">Tracking {confirmItem.china_tracking_code || "—"} · Total qty {confirmItem.quantity}</div>
              </div>
              <div>
                <label className="caption">Warehouse code</label>
                <Input value={confirmWh} onChange={e => setConfirmWh(e.target.value)} placeholder="e.g. GZ2609001" data-testid="ct-confirm-wh-code" autoFocus />
              </div>
              <div>
                <label className="caption">CBM (optional — leave blank to fill on arrival)</label>
                <Input type="number" step="0.001" value={confirmCbm} onChange={e => setConfirmCbm(e.target.value)} placeholder="Fill later if unknown" data-testid="ct-confirm-wh-cbm" />
              </div>
              <div>
                <label className="caption">Quantity to confirm (blank = all {confirmItem.quantity})</label>
                <Input type="number" min={1} max={confirmItem.quantity} value={confirmQty} onChange={e => setConfirmQty(e.target.value)} placeholder={`All ${confirmItem.quantity} — enter less to split out`} data-testid="ct-confirm-wh-qty" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmItem(null)}>Cancel</Button>
            <Button onClick={doConfirmWarehouse} data-testid="ct-confirm-wh-submit">Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Merge dialog */}
      <Dialog open={mergeOpen} onOpenChange={setMergeOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Merge boxes into one China tracking</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-slate-600">All items in the {selected.size} selected box(es) will share the same China tracking code. Rows with the same SKU + Order ID + Invoice (that haven't reached the warehouse yet) will be summed into a single row.</p>
            <div>
              <label className="caption">China tracking to merge under</label>
              <Input value={mergeTrack} onChange={e => setMergeTrack(e.target.value)} placeholder="e.g. SF-1234567890" data-testid="ct-merge-track" autoFocus />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMergeOpen(false)}>Cancel</Button>
            <Button onClick={doMerge} data-testid="ct-merge-confirm">Merge</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk-create dialog */}
      <Dialog open={bulkOpen} onOpenChange={(v) => { setBulkOpen(v); if (!v) setBulkFile(null); }}>
        <DialogContent data-testid="ct-bulk-dialog">
          <DialogHeader><DialogTitle>Bulk Create In-Transit Stocks</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="flex items-center justify-between gap-3">
              <p className="text-slate-600">For purchases made <b>before invoicing was rolled out (Sep 2026)</b> that haven't arrived yet. Columns: <span className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">sku · quantity · order_id · source · china_tracking_code · purchase_date · cny_per_pc · rm_per_pc</span>.</p>
              <Button variant="outline" size="sm" onClick={() => downloadFile("/china-tracking/bulk-create-template.xlsx", "stocks-in-transit-template.xlsx")} data-testid="ct-bulk-template">
                <Download className="w-3.5 h-3.5 mr-1" />Template
              </Button>
            </div>
            <div>
              <label className="caption">Excel file</label>
              <Input type="file" accept=".xlsx" data-testid="ct-bulk-file" onChange={e => setBulkFile(e.target.files?.[0] || null)} />
              {bulkFile && <p className="text-xs text-slate-500 mt-1">Selected: {bulkFile.name}</p>}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkOpen(false)}>Cancel</Button>
            <Button onClick={bulkCreate} data-testid="ct-bulk-confirm" className="bg-sky-600 hover:bg-sky-700" disabled={!bulkFile}>
              <Upload className="w-4 h-4 mr-1.5" />Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete box confirmation */}
      <Dialog open={!!deleteBox} onOpenChange={(v) => { if (!v) { setDeleteBox(null); setDeletePw(""); } }}>
        <DialogContent data-testid="ct-delete-dialog">
          <DialogHeader><DialogTitle className="text-rose-600">Delete box(es)?</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-slate-600">This will remove <b>{deleteBox?.label}</b> from the system. Enter admin password to confirm.</p>
            <div>
              <label className="caption">Password</label>
              <Input type="password" value={deletePw} onChange={e => setDeletePw(e.target.value)} data-testid="ct-delete-password" autoFocus
                onKeyDown={e => { if (e.key === "Enter") doDeleteBox(); }} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteBox(null)}>Cancel</Button>
            <Button onClick={doDeleteBox} data-testid="ct-delete-confirm" className="bg-rose-600 hover:bg-rose-700">
              <Trash2 className="w-4 h-4 mr-1.5" />Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
